<template>
  <v-container fluid class="pa-0">
    <h2 class="subheading grey--text">Acuerdos</h2>
    <p class="grey--text text--darken-1">{{ counts.Pendiente }} pendientes · {{ counts['En proceso'] }} en proceso · {{ counts.Completado }} completados · {{ counts.Cancelado }} cancelados</p>

    <v-row>
      <v-col cols="12" sm="6" md="3" v-for="card in summaryCards" :key="card.label">
        <v-card class="elevation-2 stat-card">
          <v-card-text>
            <v-icon :color="card.color" class="mb-2" large>{{ card.icon }}</v-icon>
            <div class="stat-value">{{ card.value }}</div>
            <div class="stat-label grey--text">{{ card.label }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="elevation-2 mt-2">
      <v-progress-linear v-if="loading" indeterminate></v-progress-linear>
      <v-card-title>
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Buscar acuerdo"
          outlined
          dense
          hide-details
          clearable
          style="max-width: 320px;"
        ></v-text-field>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="openNew()">
          <v-icon left>mdi-plus</v-icon>
          Nuevo acuerdo
        </v-btn>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="acuerdos"
        :search="search"
        :footer-props="{
          itemsPerPageOptions: [{text: '5', value: 5}, {text: '10', value: 10}, {text: 'Todos', value: -1}],
          itemsPerPageText: 'Filas por página'
        }"
        :no-data-text="'No hay acuerdos registrados'"
        class="elevation-0"
      >
        <template v-slot:item.titulo="{ item }">
          <div class="font-weight-medium">{{ item.titulo }}</div>
          <div class="caption grey--text">De: {{ item.reunionTitulo }}</div>
        </template>
        <template v-slot:item.responsable="{ item }">
          <v-avatar size="26" color="green lighten-4" class="mr-2">
            <span class="caption green--text text--darken-3 font-weight-bold">{{ initials(item.responsable) }}</span>
          </v-avatar>
          {{ item.responsable }}
        </template>
        <template v-slot:item.estado="{ item }">
          <v-chip small :color="estadoColor(item.estado)" text-color="white">{{ item.estado }}</v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon small @click="openDetail(item)">mdi-eye-outline</v-icon>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" scrollable max-width="600">
      <v-card v-if="selected" class="elevation-5 detail-card">
        <v-toolbar dark :color="estadoColor(selectedEstado)" flat class="detail-toolbar">
          <v-icon left>{{ estadoIcon(selectedEstado) }}</v-icon>
          <v-toolbar-title class="text-truncate">{{ selected.titulo }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-5">
          <div class="d-flex flex-wrap align-center mb-4">
            <v-chip small :color="estadoColor(selected.estado)" text-color="white" class="mr-2 mb-1">
              <v-icon left x-small>{{ estadoIcon(selected.estado) }}</v-icon>{{ selected.estado }}
            </v-chip>
            <v-chip small outlined color="primary" class="mr-2 mb-1">
              <v-icon left x-small>mdi-calendar-account-outline</v-icon>{{ selected.reunionTitulo }}
            </v-chip>
            <v-chip v-if="vencido" small color="red lighten-5" text-color="red darken-2" class="mb-1">
              <v-icon left x-small>mdi-alert-circle-outline</v-icon>Vencido
            </v-chip>
          </div>

          <div class="overline grey--text">Descripción</div>
          <div class="description-box mb-4">
            <span v-if="selected.descripcion">{{ selected.descripcion }}</span>
            <span v-else class="grey--text">Sin descripción</span>
          </div>

          <v-row dense>
            <v-col cols="12" sm="6">
              <div class="info-tile">
                <v-icon color="green darken-1" class="mr-3">mdi-account-outline</v-icon>
                <div>
                  <div class="overline grey--text lh-1">Responsable</div>
                  <div class="font-weight-medium">{{ selected.responsable || 'Sin asignar' }}</div>
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="info-tile" :class="{ 'info-tile--danger': vencido }">
                <v-icon :color="vencido ? 'red darken-1' : 'blue darken-1'" class="mr-3">mdi-calendar-clock</v-icon>
                <div>
                  <div class="overline grey--text lh-1">Fecha límite</div>
                  <div class="font-weight-medium" :class="{ 'red--text text--darken-1': vencido }">{{ formatDate(selected.fechaLimite) }}</div>
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="info-tile">
                <v-icon color="grey darken-1" class="mr-3">mdi-calendar-plus</v-icon>
                <div>
                  <div class="overline grey--text lh-1">Fecha de creación</div>
                  <div class="font-weight-medium">{{ formatDate(selected.created_at || selected.fechaCreacion) }}</div>
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="info-tile">
                <v-icon color="primary" class="mr-3">mdi-account-group-outline</v-icon>
                <div>
                  <div class="overline grey--text lh-1">Reunión de origen</div>
                  <div class="font-weight-medium">{{ selected.reunionTitulo }}</div>
                </div>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <div class="overline grey--text mb-2">Cambiar estado</div>
          <v-chip-group v-model="selectedStatusIndex" mandatory>
            <v-chip
              v-for="(e, i) in estados"
              :key="e"
              :color="selectedStatusIndex === i ? estadoColor(e) : ''"
              :text-color="selectedStatusIndex === i ? 'white' : ''"
              :outlined="selectedStatusIndex !== i"
            >
              <v-icon left small>{{ estadoIcon(e) }}</v-icon>{{ e }}
            </v-chip>
          </v-chip-group>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn color="grey darken-1" text @click="dialog = false">Cerrar</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed @click="guardarEstado()" :loading="loadingUpdate" :disabled="loadingUpdate || !estadoCambio">
            <v-icon left>mdi-content-save-outline</v-icon>Guardar cambios
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogNew" scrollable max-width="560" persistent>
      <v-card class="elevation-5">
        <v-toolbar dark color="primary">
          <v-toolbar-title>Nuevo acuerdo</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogNew = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-5">
          <v-form ref="formNew">
            <v-text-field v-model="form.titulo" label="Título *" :rules="[v => !!v || 'Campo obligatorio']" outlined dense></v-text-field>
            <v-textarea v-model="form.descripcion" label="Descripción" rows="3" outlined dense></v-textarea>
            <v-select
              v-model="form.reunionId"
              :items="reuniones"
              item-value="id"
              item-text="label"
              label="Reunión *"
              :rules="[v => !!v || 'Campo obligatorio']"
              :loading="loadingOptions"
              no-data-text="No hay reuniones registradas"
              outlined
              dense
            ></v-select>
            <v-select
              v-model="form.responsableId"
              :items="miembros"
              item-value="id"
              item-text="name"
              label="Responsable"
              :loading="loadingOptions"
              no-data-text="No hay integrantes activos"
              clearable
              outlined
              dense
            ></v-select>
            <v-text-field v-model="form.fechaLimite" type="date" label="Fecha límite *" :rules="[v => !!v || 'Campo obligatorio']" outlined dense></v-text-field>
            <v-select v-model="form.estado" :items="estados" label="Estado" outlined dense></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn color="grey darken-1" text @click="dialogNew = false">Cancelar</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="crearAcuerdo()" :loading="loadingCreate" :disabled="loadingCreate">Crear acuerdo</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import acuerdosService, { extractApiErrorMessage } from '@/services/acuerdosService'
import mesaDirectivaService from '@/services/mesaDirectivaService'
import { estadoColor, initials } from './mesaDirectivaMockData'

export default {
  name: 'MesaDirectivaAcuerdos',
  data () {
    return {
      acuerdos: [],
      search: '',
      dialog: false,
      selected: null,
      selectedStatusIndex: 0,
      loading: false,
      loadingUpdate: false,
      dialogNew: false,
      loadingCreate: false,
      loadingOptions: false,
      reuniones: [],
      miembros: [],
      form: { titulo: '', descripcion: '', reunionId: null, responsableId: null, fechaLimite: '', estado: 'Pendiente' },
      estados: ['Pendiente', 'En proceso', 'Completado', 'Cancelado'],
      headers: [
        { text: 'Acuerdo', value: 'titulo' },
        { text: 'Responsable', value: 'responsable' },
        { text: 'Fecha límite', value: 'fechaLimite' },
        { text: 'Estado', value: 'estado' },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ]
    }
  },
  computed: {
    selectedEstado () {
      return this.estados[this.selectedStatusIndex]
    },
    estadoCambio () {
      return this.selected && this.selectedEstado !== this.selected.estado
    },
    vencido () {
      if (!this.selected || ['Completado', 'Cancelado'].includes(this.selected.estado) || !this.selected.fechaLimite) return false
      const limite = new Date(`${String(this.selected.fechaLimite).slice(0, 10)}T23:59:59`)
      return limite < new Date()
    },
    counts () {
      const c = { Pendiente: 0, 'En proceso': 0, Completado: 0, Cancelado: 0 }
      this.acuerdos.forEach(a => { if (c[a.estado] !== undefined) c[a.estado]++ })
      return c
    },
    summaryCards () {
      return [
        { label: 'Pendientes', value: this.counts.Pendiente, icon: 'mdi-clock-outline', color: 'blue' },
        { label: 'En proceso', value: this.counts['En proceso'], icon: 'mdi-pencil-outline', color: 'orange darken-1' },
        { label: 'Completados', value: this.counts.Completado, icon: 'mdi-check-circle-outline', color: 'green' },
        { label: 'Cancelados', value: this.counts.Cancelado, icon: 'mdi-close-circle-outline', color: 'red' }
      ]
    }
  },
  async mounted () {
    await this.loadAcuerdos()
  },
  methods: {
    estadoColor,
    initials,
    estadoIcon (estado) {
      const map = {
        Pendiente: 'mdi-clock-outline',
        'En proceso': 'mdi-progress-clock',
        Completado: 'mdi-check-circle-outline',
        Cancelado: 'mdi-close-circle-outline'
      }
      return map[estado] || 'mdi-clipboard-text-outline'
    },
    formatDate (value) {
      if (!value) return '—'
      const str = String(value)
      // Fechas "YYYY-MM-DD" se interpretan como locales para evitar desfase de zona horaria
      const date = /^\d{4}-\d{2}-\d{2}$/.test(str) ? new Date(`${str}T00:00:00`) : new Date(str)
      if (isNaN(date.getTime())) return str
      return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })
    },
    async loadAcuerdos () {
      this.loading = true
      try {
        this.acuerdos = await acuerdosService.getAcuerdos()
      } catch (error) {
        const message = extractApiErrorMessage(error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', `Error al cargar acuerdos: ${message}`)
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loading = false
      }
    },
    async openNew () {
      this.form = { titulo: '', descripcion: '', reunionId: null, responsableId: null, fechaLimite: '', estado: 'Pendiente' }
      this.dialogNew = true
      this.$nextTick(() => this.$refs.formNew && this.$refs.formNew.resetValidation())
      await this.loadOptions()
    },
    async loadOptions () {
      this.loadingOptions = true
      try {
        const [meetings, members] = await Promise.all([
          mesaDirectivaService.getMeetings(),
          mesaDirectivaService.getMembers()
        ])
        this.reuniones = meetings.map(m => ({ id: m.id, label: `${m.title} — ${m.date}` }))
        this.miembros = members.filter(m => m.status === 'active')
      } catch (error) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', error.message || 'Error al cargar reuniones e integrantes')
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loadingOptions = false
      }
    },
    async crearAcuerdo () {
      if (!this.$refs.formNew.validate()) return
      this.loadingCreate = true
      try {
        await acuerdosService.createAcuerdo({
          titulo: this.form.titulo,
          descripcion: this.form.descripcion || '',
          reunionId: this.form.reunionId,
          responsableId: this.form.responsableId || null,
          fechaLimite: this.form.fechaLimite,
          estado: this.form.estado
        })
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', 'Acuerdo creado correctamente')
        this.$store.commit('snackbar_activate_true')
        this.dialogNew = false
        await this.loadAcuerdos()
      } catch (error) {
        const message = extractApiErrorMessage(error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', `Error al crear acuerdo: ${message}`)
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loadingCreate = false
      }
    },
    openDetail (item) {
      this.selected = item
      this.selectedStatusIndex = this.estados.indexOf(item.estado)
      this.dialog = true
    },
    async guardarEstado () {
      this.loadingUpdate = true
      try {
        const acuerdoActualizado = await acuerdosService.updateAcuerdo(this.selected.id, {
          titulo: this.selected.titulo,
          descripcion: this.selected.descripcion,
          responsableId: this.selected.responsableId,
          fechaLimite: this.selected.fechaLimite,
          estado: this.estados[this.selectedStatusIndex]
        })

        this.selected = acuerdoActualizado
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', 'Acuerdo actualizado correctamente')
        this.$store.commit('snackbar_activate_true')

        this.dialog = false
        await this.loadAcuerdos()
      } catch (error) {
        const message = extractApiErrorMessage(error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', `Error al actualizar acuerdo: ${message}`)
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loadingUpdate = false
      }
    }
  }
}
</script>

<style scoped>
.stat-card { text-align: center; height: 100%; }
.stat-value { font-size: 1.5rem; font-weight: 700; }
.stat-label { font-size: 0.85rem; }
.description-box { background: #f5f5f5; border-radius: 8px; padding: 12px 14px; color: #424242; white-space: pre-line; }
.info-tile { display: flex; align-items: center; background: #fafafa; border: 1px solid #eee; border-radius: 8px; padding: 10px 14px; height: 100%; }
.info-tile--danger { background: #fff5f5; border-color: #ffcdd2; }
.lh-1 { line-height: 1.2; }
</style>
